/**
 * Right-side drawer for the canvas top-right "+" button.
 *
 * N8N-style two-level navigation:
 *   1. Top-level: list of categories (cards with icon + label +
 *      description). The user clicks one to drill in.
 *   2. Inside a category: scrollable list of nodes belonging to it.
 *      A back button returns to level 1.
 *
 * A search box at the top of every level filters across ALL nodes
 * (not just the current category) — typing flips the drawer into
 * "flat search results" mode so the user doesn't have to guess the
 * right category.
 *
 * Used only by the floating top-right "+" on the canvas. The edge
 * "+" and the leaf-node "+" still pop a small inline picker
 * (``PalettePopover`` in EdgeActionsPlus.tsx) anchored at the
 * cursor — those are quick contextual actions where a full drawer
 * would be overkill.
 */
import { ArrowLeft, ChevronRight, Search } from "lucide-react";
import * as React from "react";

import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { NodeIconBadge } from "@/components/workflows/nodeIcons";
import type {
  CatalogNode,
  Direction,
  NodeKind,
  WorkflowKind,
} from "@/lib/api-workflows";
import { cn } from "@/lib/cn";


export interface PaletteDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  catalog: CatalogNode[];
  workflowKind: WorkflowKind;
  direction?: Direction;
  onPick: (kind: NodeKind) => void;
}


// Friendly category metadata. Drives the top-level cards.
// Order matters — sources first, then transforms / control flow,
// then sinks / canonical / lookups / plugins.
const CATEGORIES: Array<{
  id: string;
  label: string;
  description: string;
}> = [
  {
    id: "source",
    label: "Triggers",
    description: "Start a workflow from a webhook, schedule, file, or DB event.",
  },
  {
    id: "transform",
    label: "Data transformation",
    description: "Reshape, filter, or convert items with JSONata or JS code.",
  },
  {
    id: "control",
    label: "Flow control",
    description: "Branch, switch, loop, merge, aggregate, wait, respond.",
  },
  {
    id: "action",
    label: "Actions",
    description: "Call an external service synchronously and use its response downstream.",
  },
  {
    id: "io_canonical",
    label: "Canonical I/O",
    description: "Read or write canonical events; emit internal events.",
  },
  {
    id: "lookup",
    label: "Lookups",
    description: "Resolve values from a stored Mapping (table / DB / HTTP).",
  },
  {
    id: "sink",
    label: "Sinks",
    description: "Send the payload to an HTTP endpoint, file, or connector.",
  },
  {
    id: "plugin",
    label: "Plugins",
    description: "Invoke a filesystem-deployed Python plugin.",
  },
];


function isAllowedByDirection(node: CatalogNode, direction: Direction): boolean {
  switch (direction) {
    case "inbound":
      if (node.category === "sink") return false;
      if (node.kind === "read_canonical") return false;
      return true;
    case "internal":
      if (node.category === "source" || node.category === "sink") return false;
      return true;
    case "outbound":
      if (node.category === "source") return false;
      if (node.kind === "write_canonical") return false;
      return true;
  }
}

function isAllowedForKind(node: CatalogNode, kind: WorkflowKind): boolean {
  if (!node.kinds || node.kinds.length === 0) return true;
  return node.kinds.includes(kind);
}


export function PaletteDrawer({
  open,
  onOpenChange,
  catalog,
  workflowKind,
  direction,
  onPick,
}: PaletteDrawerProps) {
  const [query, setQuery] = React.useState("");
  const [drillCategory, setDrillCategory] = React.useState<string | null>(null);
  // ADR-0011 — third drill level: an Integration (Postgres) shown as ONE
  // palette entry that expands into its Actions/Triggers.
  const [drillIntegration, setDrillIntegration] = React.useState<string | null>(null);

  // Reset state when the drawer opens — fresh slate every time so a
  // previous filter / drilled-in view doesn't surprise the user.
  React.useEffect(() => {
    if (open) {
      setQuery("");
      setDrillCategory(null);
      setDrillIntegration(null);
    }
  }, [open]);

  // Pool of nodes allowed in this workflow.
  const allowed = React.useMemo(
    () =>
      catalog
        .filter((n) => isAllowedForKind(n, workflowKind))
        .filter((n) =>
          workflowKind === "canonical" && direction
            ? isAllowedByDirection(n, direction)
            : true,
        ),
    [catalog, workflowKind, direction],
  );

  // Group by category.
  const byCategory = React.useMemo(() => {
    const map = new Map<string, CatalogNode[]>();
    for (const n of allowed) {
      const arr = map.get(n.category) ?? [];
      arr.push(n);
      map.set(n.category, arr);
    }
    return map;
  }, [allowed]);

  // Search: flat across all categories, fuzzy-ish (substring).
  const q = query.trim().toLowerCase();
  const searchResults = React.useMemo(() => {
    if (!q) return null;
    return allowed.filter(
      (n) =>
        n.label.toLowerCase().includes(q) ||
        n.kind.toLowerCase().includes(q) ||
        (n.description ?? "").toLowerCase().includes(q),
    );
  }, [allowed, q]);

  // Categories with at least one node, in the canonical order.
  const visibleCategories = React.useMemo(
    () => CATEGORIES.filter((c) => (byCategory.get(c.id)?.length ?? 0) > 0),
    [byCategory],
  );

  const drillNodes = drillCategory ? byCategory.get(drillCategory) ?? [] : [];
  const drillMeta = CATEGORIES.find((c) => c.id === drillCategory);

  // All Actions/Triggers of the drilled-in integration (shown flat).
  const integrationNodes = drillIntegration
    ? allowed.filter((n) => n.integration === drillIntegration)
    : [];
  const integrationLabel = drillIntegration
    ? drillIntegration.charAt(0).toUpperCase() + drillIntegration.slice(1)
    : "";

  // Back link: integration → its parent view; category → categories.
  const showBack = drillIntegration !== null || drillCategory !== null;
  const goBack = () => {
    if (drillIntegration) setDrillIntegration(null);
    else setDrillCategory(null);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" width="w-[24rem]">
        <SheetHeader>
          {showBack && (
            <button
              type="button"
              onClick={goBack}
              className={cn(
                "inline-flex items-center gap-1 text-xs text-muted-foreground",
                "hover:text-foreground",
              )}
            >
              <ArrowLeft className="h-3 w-3" strokeWidth={2} />
              {drillIntegration ? "Back" : "All categories"}
            </button>
          )}
          <SheetTitle>
            {drillIntegration
              ? integrationLabel
              : drillCategory
                ? drillMeta?.label ?? "Nodes"
                : "What happens next?"}
          </SheetTitle>
        </SheetHeader>

        <SheetBody className="flex flex-col gap-3">
          {/* Search box — typing flips the drawer into flat results. */}
          <div className="relative">
            <Search
              className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground"
              strokeWidth={2}
            />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search nodes…"
              className="h-8 pl-7 text-xs"
            />
          </div>

          {/* Render branches, mutually exclusive (most specific first):
                * drilled into an integration → its Actions/Triggers (flat)
                * search active → entries (integrations collapsed to one)
                * drilled into a category → entries
                * default → top-level category cards */}
          {drillIntegration ? (
            // ADR-0011 — split the integration into Actions vs Triggers
            // (n8n model) rather than one flat list.
            <IntegrationDetail nodes={integrationNodes} onPick={onPick} />
          ) : q ? (
            <EntryList
              nodes={searchResults ?? []}
              onPick={onPick}
              onDrillIntegration={setDrillIntegration}
              emptyHint={`No nodes match "${q}".`}
            />
          ) : drillCategory ? (
            <>
              {drillMeta?.description && (
                <p className="text-[11px] text-muted-foreground">
                  {drillMeta.description}
                </p>
              )}
              <EntryList
                nodes={drillNodes}
                onPick={onPick}
                onDrillIntegration={setDrillIntegration}
              />
            </>
          ) : (
            <ul className="-mx-1 space-y-1">
              {visibleCategories.map((cat) => (
                <li key={cat.id}>
                  <button
                    type="button"
                    onClick={() => setDrillCategory(cat.id)}
                    className={cn(
                      "flex w-full items-start gap-3 rounded-md px-3 py-2 text-left",
                      "border border-transparent hover:border-border hover:bg-accent/10",
                      "transition-colors",
                    )}
                  >
                    {/* Use the first node of the category as the icon
                        representative — saves us from maintaining a
                        separate category-icon map. */}
                    <NodeIconBadge
                      kind={(byCategory.get(cat.id) ?? [])[0]?.kind}
                      category={cat.id}
                      size={32}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-foreground">
                        {cat.label}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {cat.description}
                      </div>
                    </div>
                    <ChevronRight
                      className="mt-1 h-4 w-4 shrink-0 text-muted-foreground"
                      strokeWidth={2}
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}


/** Split nodes into standalone + integration groups (ADR-0011) so a
 *  connector's Actions render nested under one "Postgres" sub-header,
 *  n8n-style, instead of flat. */
function partitionByIntegration(nodes: CatalogNode[]): {
  standalone: CatalogNode[];
  integrations: Array<{ id: string; label: string; nodes: CatalogNode[] }>;
} {
  const standalone: CatalogNode[] = [];
  const byId = new Map<string, CatalogNode[]>();
  for (const n of nodes) {
    if (n.integration) {
      const arr = byId.get(n.integration) ?? [];
      arr.push(n);
      byId.set(n.integration, arr);
    } else {
      standalone.push(n);
    }
  }
  const integrations = Array.from(byId.entries()).map(([id, ns]) => ({
    id,
    label: id.charAt(0).toUpperCase() + id.slice(1),
    nodes: ns,
  }));
  return { standalone, integrations };
}

/** "6 actions · 1 trigger" summary for an integration row (ADR-0011). */
function integrationSummary(nodes: CatalogNode[]): string {
  const triggers = nodes.filter((n) => n.is_trigger).length;
  const actions = nodes.length - triggers;
  const parts: string[] = [];
  if (actions > 0) parts.push(`${actions} action${actions === 1 ? "" : "s"}`);
  if (triggers > 0) parts.push(`${triggers} trigger${triggers === 1 ? "" : "s"}`);
  return parts.join(" · ") || "0 actions";
}

function NodeButton({
  n,
  onPick,
}: {
  n: CatalogNode;
  onPick: (kind: NodeKind) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onPick(n.kind)}
      className={cn(
        "flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left",
        "border border-transparent hover:border-border hover:bg-accent/10",
        "transition-colors",
      )}
      title={n.description ?? ""}
    >
      <NodeIconBadge kind={n.kind} category={n.category} size={24} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium text-foreground">
          {n.label}
        </div>
        {n.description && (
          <div className="line-clamp-1 text-[10px] text-muted-foreground">
            {n.description}
          </div>
        )}
      </div>
    </button>
  );
}

/** Integration drill-in (ADR-0011) — splits the integration's nodes into
 *  "Actions (N)" and "Triggers (M)" sections, n8n-style. */
function IntegrationDetail({
  nodes,
  onPick,
}: {
  nodes: CatalogNode[];
  onPick: (kind: NodeKind) => void;
}) {
  const actions = nodes.filter((n) => !n.is_trigger);
  const triggers = nodes.filter((n) => n.is_trigger);
  return (
    <div className="-mx-1 space-y-3">
      {actions.length > 0 && (
        <div>
          <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Actions ({actions.length})
          </div>
          <ul className="space-y-1">
            {actions.map((n) => (
              <li key={n.kind}>
                <NodeButton n={n} onPick={onPick} />
              </li>
            ))}
          </ul>
        </div>
      )}
      {triggers.length > 0 && (
        <div>
          <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Triggers ({triggers.length})
          </div>
          <ul className="space-y-1">
            {triggers.map((n) => (
              <li key={n.kind}>
                <NodeButton n={n} onPick={onPick} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Entry list: standalone node rows + integration rows (ADR-0011). A
 *  connector's Actions collapse into ONE clickable "Postgres" row that
 *  drills into them — so the palette and search show the integration as a
 *  single entry, n8n-style. */
function EntryList({
  nodes,
  onPick,
  onDrillIntegration,
  emptyHint,
}: {
  nodes: CatalogNode[];
  onPick: (kind: NodeKind) => void;
  onDrillIntegration: (id: string) => void;
  emptyHint?: string;
}) {
  if (nodes.length === 0) {
    return (
      <div className="px-1 py-3 text-center text-xs text-muted-foreground">
        {emptyHint ?? "No nodes available."}
      </div>
    );
  }
  const { standalone, integrations } = partitionByIntegration(nodes);
  return (
    <ul className="-mx-1 space-y-1">
      {standalone.map((n) => (
        <li key={n.kind}>
          <NodeButton n={n} onPick={onPick} />
        </li>
      ))}
      {integrations.map((intg) => {
        const summary = integrationSummary(intg.nodes);
        return (
          <li key={`intg:${intg.id}`}>
            <button
              type="button"
              onClick={() => onDrillIntegration(intg.id)}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left",
                "border border-transparent hover:border-border hover:bg-accent/10",
                "transition-colors",
              )}
              title={`${intg.label} — ${summary}`}
            >
              <NodeIconBadge kind={intg.nodes[0]?.kind} category="action" size={24} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-medium text-foreground">
                  {intg.label}
                </div>
                <div className="text-[10px] text-muted-foreground">{summary}</div>
              </div>
              <ChevronRight
                className="h-4 w-4 shrink-0 text-muted-foreground"
                strokeWidth={2}
              />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
